import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { fetchUvuajiInvestments, deleteAdminInvestment } from './hooks/hooks';
import {
  FiTrash2, FiChevronDown, FiSearch, FiChevronLeft, FiChevronRight,
  FiX, FiMapPin, FiShoppingBag, FiUser, FiBarChart2, FiAlertTriangle,
} from 'react-icons/fi';
import { MdOutlineStorefront } from 'react-icons/md';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = d => {
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const initials = n => n?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const daysBetween = (s, e) => Math.max(0, Math.round((new Date(e) - new Date(s)) / (1000 * 60 * 60 * 24)));

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar = ({ name }) => (
  <div className="w-8 h-8 rounded-full bg-[#001F3F] flex items-center justify-center text-[11px] font-semibold text-[#C9982A] shrink-0">
    {initials(name)}
  </div>
);

// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ active, pickup }) => {
  if (pickup !== undefined) {
    return pickup ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#EEF2F8] text-[#001F3F]">
        <MdOutlineStorefront size={11} /> Pick up
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800">
        <FiMapPin size={11} /> Delivery
      </span>
    );
  }
  return active ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-800">Active</span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500">Completed</span>
  );
};

// ─── Info Row ─────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, valueClassName }) => (
  <div className="flex justify-between items-start py-1.5 border-b border-gray-100 gap-2 last:border-0">
    <span className="text-[11px] text-gray-500 shrink-0">{label}</span>
    <span className={`text-[12px] font-semibold text-[#001F3F] text-right ${valueClassName || ''}`}>{value}</span>
  </div>
);

// ─── Panel Section ────────────────────────────────────────────────────────────
const PanelSection = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-3">
    <div className="flex items-center gap-1.5 mb-2.5">
      <Icon size={13} className="text-[#001F3F]" />
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{title}</span>
    </div>
    {children}
  </div>
);

// ─── Expanded Row Panel ───────────────────────────────────────────────────────
const ExpandedPanel = ({ inv }) => {
  const days = daysBetween(inv.start_date, inv.end_date);
  const isDeliver = inv.delivery_mode === 'deliver';

  return (
    <div className="bg-emerald-50/40 border-l-4 border-emerald-500 p-4">
      <div className="grid grid-cols-3 gap-3">
        <PanelSection icon={FiUser} title="Client info">
          <InfoRow label="Full name" value={inv.user_name} />
          <InfoRow label="Email" value={inv.user_email} valueClassName="text-[11px] break-all" />
          <InfoRow label="Investment ID" value={`#${inv.id}`} />
          <InfoRow
            label="Risk accepted"
            value={inv.risk_accepted ? 'Yes' : 'No'}
            valueClassName={inv.risk_accepted ? 'text-emerald-600' : 'text-red-600'}
          />
        </PanelSection>

        <PanelSection icon={FiBarChart2} title="Investment summary">
          <InfoRow label="Asset" value={inv.asset_name} />
          <InfoRow label="Quantity" value={`${inv.quantity} unit${inv.quantity > 1 ? 's' : ''}`} valueClassName="text-emerald-600" />
          <InfoRow label="Duration" value={`${days} days`} />
          <InfoRow label="Start date" value={fmtDate(inv.start_date)} />
          <InfoRow label="End date" value={fmtDate(inv.end_date)} />
          <div className="pt-2"><Badge active={inv.is_active} /></div>
        </PanelSection>

        {isDeliver ? (
          <PanelSection icon={FiMapPin} title="Delivery address">
            <InfoRow label="Recipient" value={inv.recipient_name || '—'} />
            <InfoRow label="Phone" value={inv.recipient_phone || '—'} />
            <InfoRow label="Region" value={inv.region || '—'} />
            <InfoRow label="Address" value={inv.address || '—'} />
            <InfoRow label="Landmark" value={inv.landmark || '—'} />
          </PanelSection>
        ) : (
          <PanelSection icon={FiShoppingBag} title="Pick up info">
            <InfoRow label="Mode" value={<Badge pickup={true} />} />
            <InfoRow label="Location" value="Urithi Office" />
            <InfoRow
              label="Risk accepted"
              value={inv.risk_accepted ? 'Yes' : 'No'}
              valueClassName={inv.risk_accepted ? 'text-emerald-600' : 'text-gray-400'}
            />
          </PanelSection>
        )}
      </div>
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, valueClassName }) => (
  <div className="bg-gray-50 rounded-lg px-4 py-2.5 flex-1">
    <div className="text-[11px] text-gray-500 mb-1">{label}</div>
    <div className={`text-[22px] font-bold ${valueClassName || 'text-[#001F3F]'}`}>{value}</div>
  </div>
);

// ─── Delete Modal ─────────────────────────────────────────────────────────────
const DeleteModal = ({ investment, onClose, onConfirm, isLoading }) => {
  const [refund, setRefund] = useState(true);
  const [restore, setRestore] = useState(true);

  return (
    <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100">
          <span className="flex items-center gap-1.5 text-[13px] font-bold text-red-600">
            <FiAlertTriangle size={14} /> Delete investment
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex items-center">
            <FiX size={16} />
          </button>
        </div>

        <div className="p-4">
          <p className="text-[12px] text-gray-500 mb-3">
            Are you sure you want to delete this investment? Configure options below.
          </p>

          <div className="bg-gray-50 rounded-lg px-3 py-2.5 mb-4">
            {[['Client', investment.user_name], ['Asset', investment.asset_name], ['Quantity', `${investment.quantity} unit${investment.quantity > 1 ? 's' : ''}`]].map(([l, v]) => (
              <div key={l} className="flex justify-between py-1 text-[12px]">
                <span className="text-gray-500">{l}</span>
                <span className="font-semibold text-[#001F3F]">{v}</span>
              </div>
            ))}
          </div>

          {[
            { label: "Refund user's investment amount", val: refund, set: setRefund },
            { label: 'Restore quantity to asset inventory', val: restore, set: setRestore },
          ].map(opt => (
            <label key={opt.label} className="flex items-center gap-2 mb-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={opt.val}
                onChange={e => opt.set(e.target.checked)}
                className="accent-emerald-600 w-3.5 h-3.5"
              />
              <span className="text-[12px] text-[#001F3F]">{opt.label}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-2 px-4 py-3 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-[12px] font-semibold text-[#001F3F] cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ refund, restore })}
            disabled={isLoading}
            className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-[12px] font-bold cursor-pointer disabled:opacity-60"
          >
            {isLoading ? 'Deleting…' : 'Delete investment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, severity }) => (
  <div className={`fixed bottom-6 right-6 z-[100] px-4 py-2.5 rounded-xl text-[12px] font-semibold border ${
    severity === 'success'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : 'bg-red-50 text-red-800 border-red-200'
  }`}>
    {message}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
function UvunajiInvestment() {
  const queryClient = useQueryClient();
  const { data: investments, isLoading, isError, refetch } = useQuery({
    queryKey: ['investments'],
    queryFn: fetchUvuajiInvestments,
  });

  const [searchQuery,  setSearchQuery]  = useState('');
  const [sortBy,       setSortBy]       = useState('date');
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(5);
  const [expandedId,   setExpandedId]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast,        setToast]        = useState(null);

  const showToast = (message, severity) => {
    setToast({ message, severity });
    setTimeout(() => setToast(null), 3500);
  };

  const deleteMutation = useMutation({
    mutationFn: ({ id, refund, restore }) => deleteAdminInvestment(id, refund, restore),
    onSuccess: () => {
      queryClient.invalidateQueries(['investments']);
      setDeleteTarget(null);
      refetch();
      showToast('Investment deleted successfully', 'success');
    },
    onError: err => showToast(err.response?.data?.error || 'Failed to delete investment', 'error'),
  });

  const allInvestments = Array.isArray(investments) ? investments : [];

  const filtered = allInvestments
    .filter(item =>
      item.asset_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => sortBy === 'asset_name'
      ? a.asset_name.localeCompare(b.asset_name)
      : new Date(b.start_date) - new Date(a.start_date)
    );

  const totalPages    = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated     = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const activeCount   = allInvestments.filter(r => r.is_active).length;
  const deliveryCount = allInvestments.filter(r => r.delivery_mode === 'deliver').length;
  const pickupCount   = allInvestments.filter(r => r.delivery_mode === 'pickup').length;

  const toggleExpand = id => setExpandedId(prev => prev === id ? null : id);

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
      <div className="w-9 h-9 border-[3px] border-gray-200 border-t-[#001F3F] rounded-full animate-spin" />
      <p className="text-[13px] text-gray-500">Loading investments…</p>
    </div>
  );

  if (isError) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-sm">
        <p className="text-[13px] font-bold text-red-600 mb-1">Failed to load investments</p>
        <p className="text-[12px] text-red-700">Please check your connection and try again.</p>
        <button
          onClick={refetch}
          className="mt-3 px-4 py-2 bg-[#001F3F] text-white text-[12px] font-semibold rounded-lg"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full px-4 py-6">

      {/* Page heading */}
      <div className="mb-5">
        <h1 className="text-[16px] font-extrabold text-[#001F3F] uppercase tracking-[0.4px] mb-1">
          Investment (Rental) Overview
        </h1>
        <p className="text-[12px] text-gray-500">View detailed investment records along with user information.</p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-2.5 mb-5">
        <StatCard label="Total investments" value={allInvestments.length} />
        <StatCard label="Active"            value={activeCount}            valueClassName="text-emerald-600" />
        <StatCard label="Home delivery"     value={deliveryCount}          valueClassName="text-[#C9982A]" />
        <StatCard label="Pick up"           value={pickupCount}            valueClassName="text-[#001F3F]" />
      </div>

      {/* Table card */}
      <div className="w-full bg-white border border-gray-200 rounded-2xl overflow-hidden">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2.5 px-4 py-3.5 border-b border-gray-100">
          <h2 className="text-[13px] font-bold text-[#001F3F] uppercase tracking-[0.4px] flex-1 min-w-[140px]">
            All Investments
          </h2>
          <div className="relative flex-1 min-w-[220px]">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search by asset or client…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
              className="w-full pl-8 pr-3 py-2 text-[12px] border border-gray-200 rounded-lg bg-gray-50 text-[#001F3F] outline-none focus:ring-1 focus:ring-[#001F3F]"
            />
          </div>
          <select
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setPage(0); }}
            className="px-3 py-2 text-[12px] border border-gray-200 rounded-lg bg-gray-50 text-[#001F3F] outline-none"
          >
            <option value="date">Sort: Start date</option>
            <option value="asset_name">Sort: Asset name</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col className="w-[22%]" /><col className="w-[18%]" /><col className="w-[6%]" />
              <col className="w-[11%]" /><col className="w-[11%]" /><col className="w-[11%]" />
              <col className="w-[10%]" /><col className="w-[7%]" />
            </colgroup>
            <thead>
              <tr>
                {['Client info', 'Asset', 'Qty', 'Start date', 'End date', 'Delivery', 'Status', ''].map((h, i) => (
                  <th
                    key={i}
                    className="px-3.5 py-2.5 text-left text-[11px] font-semibold text-gray-400 bg-gray-50 uppercase tracking-[0.5px] border-b border-gray-100 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3.5 py-12 text-center text-[12px] text-gray-400">
                    No investments found
                  </td>
                </tr>
              ) : paginated.map(inv => {
                const isOpen = expandedId === inv.id;
                return (
                  <React.Fragment key={inv.id}>
                    <tr
                      onClick={() => toggleExpand(inv.id)}
                      className={`cursor-pointer transition-colors duration-100 ${isOpen ? 'bg-emerald-50/60' : 'bg-white hover:bg-gray-50'}`}
                    >
                      {/* Client */}
                      <td className="px-3.5 py-3 text-[12px] text-[#001F3F] border-b border-gray-100 align-middle">
                        <div className="flex items-center gap-2">
                          <Avatar name={inv.user_name} />
                          <div className="min-w-0">
                            <div className="text-[12px] font-semibold text-[#001F3F] truncate">{inv.user_name}</div>
                            <div className="text-[11px] text-gray-400 truncate">{inv.user_email}</div>
                          </div>
                        </div>
                      </td>
                      {/* Asset */}
                      <td className="px-3.5 py-3 text-[12px] font-semibold text-[#001F3F] border-b border-gray-100 align-middle truncate">{inv.asset_name}</td>
                      {/* Qty */}
                      <td className="px-3.5 py-3 text-[12px] text-[#001F3F] border-b border-gray-100 align-middle">{inv.quantity}</td>
                      {/* Start */}
                      <td className="px-3.5 py-3 text-[12px] text-[#001F3F] border-b border-gray-100 align-middle">{fmtDate(inv.start_date)}</td>
                      {/* End */}
                      <td className="px-3.5 py-3 text-[12px] text-[#001F3F] border-b border-gray-100 align-middle">{fmtDate(inv.end_date)}</td>
                      {/* Delivery */}
                      <td className="px-3.5 py-3 border-b border-gray-100 align-middle">
                        <Badge pickup={inv.delivery_mode === 'pickup'} />
                      </td>
                      {/* Status */}
                      <td className="px-3.5 py-3 border-b border-gray-100 align-middle">
                        <Badge active={inv.is_active} />
                      </td>
                      {/* Actions */}
                      <td className="px-3.5 py-3 border-b border-gray-100 align-middle">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={e => { e.stopPropagation(); setDeleteTarget(inv); }}
                            disabled={deleteMutation.isPending}
                            aria-label="Delete investment"
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50 flex items-center"
                          >
                            <FiTrash2 size={14} />
                          </button>
                          <span className={`text-gray-400 flex items-center transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                            <FiChevronDown size={14} />
                          </span>
                        </div>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={8} className="p-0 border-b border-gray-100">
                          <ExpandedPanel inv={inv} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between px-4 py-2.5 border-t border-gray-100 gap-2">
          <span className="text-[12px] text-gray-400">
            Showing {filtered.length === 0 ? 0 : page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filtered.length)} of {filtered.length} results
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-gray-400">Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              className="px-2 py-1.5 text-[12px] border border-gray-200 rounded-md bg-gray-50 text-[#001F3F] outline-none"
            >
              {[5, 10, 25].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="p-1.5 border border-gray-200 rounded-md bg-white disabled:opacity-40 flex items-center"
            >
              <FiChevronLeft size={14} />
            </button>
            <span className="text-[12px] text-gray-400">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="p-1.5 border border-gray-200 rounded-md bg-white disabled:opacity-40 flex items-center"
            >
              <FiChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Delete modal */}
      {deleteTarget && (
        <DeleteModal
          investment={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          onConfirm={({ refund, restore }) => deleteMutation.mutate({ id: deleteTarget.id, refund, restore })}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} severity={toast.severity} />}
    </div>
  );
}

export default UvunajiInvestment;
// import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// import React, { useState } from 'react'
// import { fetchUvuajiInvestments, deleteAdminInvestment } from './hooks/hooks';
// import { FiTrash2, FiEye, FiX, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// function UvunajiInvestment() {
//   const queryClient = useQueryClient();
//   const { data: investments, isLoading, isError, refetch } = useQuery({
//     queryKey: ["investments"],
//     queryFn: fetchUvuajiInvestments,
//   });

//   const [searchQuery, setSearchQuery] = useState("");
//   const [sortBy, setSortBy] = useState("date");
//   const [page, setPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(5);
//   const [selectedInvestment, setSelectedInvestment] = useState(null);
//   const [detailsOpen, setDetailsOpen] = useState(false);
//   const [deleteOpen, setDeleteOpen] = useState(false);
//   const [refundOption, setRefundOption] = useState(true);
//   const [restoreOption, setRestoreOption] = useState(true);
//   const [toast, setToast] = useState({ open: false, message: "", severity: "" });

//   const filteredInvestments = Array.isArray(investments) ? investments?.filter(item =>
//     item.asset_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
//     item.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
//   ) : [];

//   const sortedNews = [...filteredInvestments].sort((a, b) => {
//     if (sortBy === "asset_name") {
//       return a.asset_name.localeCompare(b.asset_name);
//     } else {
//       return new Date(b.start_date) - new Date(a.start_date);
//     }
//   });

//   const handleChangePage = (newPage) => setPage(newPage);
//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10));
//     setPage(0);
//   };

//   // Delete mutation
//   const deleteMutation = useMutation({
//     mutationFn: ({ id, refund, restore }) => deleteAdminInvestment(id, refund, restore),
//     onSuccess: () => {
//       queryClient.invalidateQueries(['investments']);
//       setDeleteOpen(false);
//       setSelectedInvestment(null);
//       setToast({ open: true, message: "Investment deleted successfully!", severity: "success" });
//       refetch();
//     },
//     onError: (error) => {
//       setToast({ open: true, message: error.response?.data?.error || "Failed to delete investment", severity: "error" });
//     }
//   });

//   const handleDeleteClick = (investment) => {
//     setSelectedInvestment(investment);
//     setDeleteOpen(true);
//   };

//   const confirmDelete = () => {
//     if (selectedInvestment) {
//       deleteMutation.mutate({
//         id: selectedInvestment.id,
//         refund: refundOption,
//         restore: restoreOption
//       });
//     }
//   };

//   const handleViewDetails = (investment) => {
//     setSelectedInvestment(investment);
//     setDetailsOpen(true);
//   };

//   // Pagination calculations
//   const paginatedData = sortedNews.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
//   const totalPages = Math.ceil(sortedNews.length / rowsPerPage);

//   if (isLoading) {
//     return (
//       <div className="flex items-center justify-center min-h-screen">
//         <div className="text-center">
//           <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
//           <p className="mt-4 text-gray-600 text-sm">Loading...</p>
//         </div>
//       </div>
//     );
//   }

//   if (isError) {
//     return (
//       <div className="flex items-center justify-center min-h-screen">
//         <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
//           <h3 className="text-red-800 font-semibold mb-2 text-sm">Error Loading Investments</h3>
//           <p className="text-red-600 text-sm">Error fetching investments. Please try again later.</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
//       {investments?.length === 0 ? (
//         <p className="text-center text-gray-600 text-xs capitalize">No Data yet</p>
//       ) : (
//         <>
//           {/* Header */}
//           <h1 className="text-xs font-bold text-gray-900 uppercase">Investment(Rental) Overview</h1>
//           <p className="text-xs text-gray-600 capitalize mb-2">View detailed investment records along with user information.</p>
          
//           <h2 className="text-xs font-semibold uppercase text-gray-900 mt-4 mb-2">All Investments(Rentals)</h2>

//           {/* Search and Sort */}
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//             <div className="md:col-span-2">
//               <div className="relative">
//                 <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
//                 <input
//                   type="text"
//                   placeholder="Search by Asset Name/ Client Name..."
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                   className="w-full pl-10 pr-3 py-2 text-xs bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-900 focus:border-blue-900"
//                 />
//               </div>
//             </div>
//             <div>
//               <select
//                 value={sortBy}
//                 onChange={(e) => setSortBy(e.target.value)}
//                 className="w-full px-3 py-2 text-xs bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-900 focus:border-blue-900"
//               >
//                 <option value="date">start Date</option>
//                 <option value="asset_name">Asset name</option>
//               </select>
//             </div>
//           </div>

//           {/* Table */}
//           <div className="overflow-x-auto bg-white rounded-lg shadow">
//             <table className="min-w-full divide-y divide-gray-200">
//               <thead className="bg-gray-50">
//                 <tr>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Client Info</th>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Asset Name</th>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Quantity</th>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Start Date</th>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">End Date</th>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Active</th>
//                   <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="bg-white divide-y divide-gray-200">
//                 {paginatedData.map((investment) => (
//                   <tr key={investment.id} className="hover:bg-gray-50">
//                     <td className="px-4 py-3">
//                       <div>
//                         <p className="text-xs font-semibold text-gray-900 capitalize">{investment.user_name}</p>
//                         <p className="text-xs font-semibold text-gray-600">{investment.user_email}</p>
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 text-xs font-semibold text-gray-900 capitalize">{investment.asset_name}</td>
//                     <td className="px-4 py-3 text-xs font-semibold text-gray-900">{investment.quantity}</td>
//                     <td className="px-4 py-3 text-xs font-semibold text-gray-900">
//                       {new Date(investment.start_date).toLocaleDateString()}
//                     </td>
//                     <td className="px-4 py-3 text-xs font-semibold text-gray-900">
//                       {new Date(investment.end_date).toLocaleDateString() || "N/A"}
//                     </td>
//                     <td className="px-4 py-3">
//                       <span className={`text-xs font-semibold capitalize ${
//                         investment.is_active ? 'text-green-600' : 'text-gray-400'
//                       }`}>
//                         {investment.is_active ? "Yes" : "No"}
//                       </span>
//                     </td>
//                     <td className="px-4 py-3">
//                       <div className="flex gap-2">
//                         <button
//                           onClick={() => handleViewDetails(investment)}
//                           className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
//                         >
//                           <FiEye className="h-4 w-4" />
//                         </button>
//                         <button
//                           onClick={() => handleDeleteClick(investment)}
//                           disabled={deleteMutation.isLoading}
//                           className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
//                         >
//                           <FiTrash2 className="h-4 w-4" />
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {/* Pagination */}
//           <div className="mt-4 flex items-center justify-between">
//             <div className="text-xs text-gray-600">
//               Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, sortedNews.length)} of {sortedNews.length} results
//             </div>
//             <div className="flex items-center gap-2">
//               <span className="text-xs text-gray-600">Rows per page:</span>
//               <select
//                 value={rowsPerPage}
//                 onChange={handleChangeRowsPerPage}
//                 className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-900"
//               >
//                 <option value={5}>5</option>
//                 <option value={10}>10</option>
//                 <option value={25}>25</option>
//               </select>
//               <button
//                 onClick={() => handleChangePage(page - 1)}
//                 disabled={page === 0}
//                 className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 <FiChevronLeft className="h-4 w-4" />
//               </button>
//               <span className="text-xs text-gray-600">
//                 {page + 1} / {totalPages || 1}
//               </span>
//               <button
//                 onClick={() => handleChangePage(page + 1)}
//                 disabled={page >= totalPages - 1}
//                 className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 <FiChevronRight className="h-4 w-4" />
//               </button>
//             </div>
//           </div>
//         </>
//       )}

//       {/* View Details Modal */}
//       {detailsOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
//             <div className="flex items-center justify-between p-4 border-b border-gray-200">
//               <h3 className="text-xs font-bold uppercase text-gray-900">Investment Details</h3>
//               <button
//                 onClick={() => setDetailsOpen(false)}
//                 className="p-1 hover:bg-gray-100 rounded transition-colors"
//               >
//                 <FiX className="h-5 w-5" />
//               </button>
//             </div>
//             <div className="p-6 space-y-4">
//               {selectedInvestment && (
//                 <>
//                   <div>
//                     <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Client Information</h4>
//                     <div className="p-4 bg-gray-50 rounded space-y-1">
//                       <p className="text-xs"><strong>Name:</strong> {selectedInvestment.user_name}</p>
//                       <p className="text-xs"><strong>Email:</strong> {selectedInvestment.user_email}</p>
//                     </div>
//                   </div>

//                   <div>
//                     <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Investment Information</h4>
//                     <div className="p-4 bg-gray-50 rounded">
//                       <div className="grid grid-cols-2 gap-4">
//                         <div>
//                           <p className="text-xs"><strong>Asset Name:</strong> {selectedInvestment.asset_name}</p>
//                         </div>
//                         <div>
//                           <p className="text-xs"><strong>Quantity:</strong> {selectedInvestment.quantity} units</p>
//                         </div>
//                         <div>
//                           <p className="text-xs"><strong>Start Date:</strong> {new Date(selectedInvestment.start_date).toLocaleDateString()}</p>
//                         </div>
//                         <div>
//                           <p className="text-xs"><strong>End Date:</strong> {new Date(selectedInvestment.end_date).toLocaleDateString()}</p>
//                         </div>
//                         <div>
//                           <p className="text-xs">
//                             <strong>Status:</strong>{' '}
//                             <span className={selectedInvestment.is_active ? 'text-green-600' : 'text-gray-400'}>
//                               {selectedInvestment.is_active ? 'Active' : 'Completed'}
//                             </span>
//                           </p>
//                         </div>
//                         <div>
//                           <p className="text-xs"><strong>Investment ID:</strong> #{selectedInvestment.id}</p>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </>
//               )}
//             </div>
//             <div className="flex justify-end p-4 border-t border-gray-200">
//               <button
//                 onClick={() => setDetailsOpen(false)}
//                 className="px-4 py-2 text-xs font-medium text-blue-900 hover:bg-blue-50 rounded transition-colors capitalize"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       {deleteOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg max-w-md w-full">
//             <div className="p-6">
//               <h3 className="text-xs font-bold uppercase text-red-600 mb-2">Delete Investment</h3>
//               <p className="text-xs text-gray-600 mb-4">
//                 Are you sure you want to delete this investment? Configure the options below:
//               </p>
              
//               {selectedInvestment && (
//                 <div className="p-4 bg-gray-50 rounded mb-4 space-y-1">
//                   <p className="text-xs"><strong>Client:</strong> {selectedInvestment.user_name}</p>
//                   <p className="text-xs"><strong>Asset:</strong> {selectedInvestment.asset_name}</p>
//                   <p className="text-xs"><strong>Quantity:</strong> {selectedInvestment.quantity} units</p>
//                 </div>
//               )}

//               <div className="space-y-2">
//                 <label className="flex items-center">
//                   <input
//                     type="checkbox"
//                     checked={refundOption}
//                     onChange={(e) => setRefundOption(e.target.checked)}
//                     className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                   />
//                   <span className="ml-2 text-xs text-gray-700">Refund user's investment amount</span>
//                 </label>
//                 <label className="flex items-center">
//                   <input
//                     type="checkbox"
//                     checked={restoreOption}
//                     onChange={(e) => setRestoreOption(e.target.checked)}
//                     className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                   />
//                   <span className="ml-2 text-xs text-gray-700">Restore quantity to asset inventory</span>
//                 </label>
//               </div>
//             </div>
            
//             <div className="flex gap-3 p-4 border-t border-gray-200">
//               <button
//                 onClick={() => setDeleteOpen(false)}
//                 disabled={deleteMutation.isLoading}
//                 className="flex-1 px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors capitalize disabled:opacity-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 disabled={deleteMutation.isLoading}
//                 className="flex-1 px-4 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors capitalize disabled:opacity-50"
//               >
//                 {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Toast Notification */}
//       {toast.open && (
//         <div className="fixed bottom-4 right-4 z-50">
//           <div className={`px-4 py-3 rounded shadow-lg ${
//             toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
//           } text-white text-xs font-medium capitalize`}>
//             {toast.message}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default UvunajiInvestment;